/**
 * The two module vocabularies, and the one translation between them.
 *
 * Product modules call themselves one thing and Auth's module catalogue calls
 * them another. For four of the five modules both spellings are identical,
 * which is exactly why the one that differs keeps going unnoticed: `kpi` in
 * playbook-be and the shell is `kpi_manager` in Auth.
 *
 * A wrong key does not throw. It compares equal to nothing, so whatever the
 * caller was gating stays silently shut (or silently open, depending on which
 * way the test runs) with no error anywhere. That has already happened once:
 * notification stored a `kpi.*` row under the producer's own id and the rows
 * never reached the bell (PB-2946).
 *
 * So the translation lives HERE, once, and every boundary that crosses between
 * the two vocabularies goes through it rather than re-deriving the mapping.
 */
/** Every product module id, in the product vocabulary. */
export const PRODUCT_MODULE_IDS = [
    "solve",
    "vsm",
    "hoshin",
    "kpi",
    "lama",
];
/**
 * Auth catalogue keys that are NOT product modules.
 *
 * The catalogue is WIDER than the two vocabularies below, and always has been:
 * `knowledge_studio` and `rbta` are catalogue keys with no `ProductModuleId`.
 * `GET /api/auth/me` serves the enabled catalogue set verbatim, so a platform
 * capability can be governed per tenant and gated on that claim without
 * pretending to be an app — no route, no manifest, no launcher entry.
 *
 * Declared here for one reason: a wrong module key does not throw, it compares
 * equal to nothing and leaves the gate silently shut or silently open. That has
 * shipped once already (PB-2946). Two services and the shell gate on this key,
 * so it gets one spelling rather than three string literals.
 *
 * Deliberately NOT added to `AUTH_MODULE_KEYS`: that array is held 1:1 with
 * `PRODUCT_MODULE_IDS` by a type-exhaustive translation record and tests
 * asserting the two cover each other, so adding a capability there would mean
 * inventing a sixth product app.
 */
export const EMAIL_MODULE_KEY = "email";
/** Every Auth catalogue key. This is the vocabulary a tenant's enabled set uses. */
export const AUTH_MODULE_KEYS = [
    "solve",
    "vsm",
    "hoshin",
    "kpi_manager",
    "lama",
];
/**
 * Product module id → the Auth catalogue key it is enabled under.
 *
 * Exhaustive by type: adding a `ProductModuleId` without adding its Auth key
 * fails to compile rather than silently resolving to `undefined` at a gate.
 */
export const AUTH_MODULE_KEY_BY_PRODUCT_MODULE_ID = {
    solve: "solve",
    vsm: "vsm",
    hoshin: "hoshin",
    kpi: "kpi_manager",
    lama: "lama",
};
export function isProductModuleId(value) {
    return Object.hasOwn(AUTH_MODULE_KEY_BY_PRODUCT_MODULE_ID, value);
}
/**
 * Translate a product module id into the key Auth answers on.
 *
 * Returns `null` for anything unknown rather than echoing the input back: an
 * unrecognised id passed straight through is precisely the shape that produces
 * a silent never-match at the far end, and a caller gating on this must be able
 * to tell "not a product module" apart from "a product module that is off".
 */
export function authModuleKeyFor(productModuleId) {
    return isProductModuleId(productModuleId)
        ? AUTH_MODULE_KEY_BY_PRODUCT_MODULE_ID[productModuleId]
        : null;
}
/**
 * Player AI workspace type → the product module that owns it.
 *
 * Player AI addresses a workspace by type, and only one of those spellings
 * matches its module: a Hoshin workshop is `hoshin_workshop`. Unknown types
 * answer `null` so a caller gates on "no owning module" deliberately rather
 * than passing an unrecognised string to a comparison that can never match.
 */
/*
 * Exported from this module for its own spec, but deliberately NOT re-exported
 * from the package index: `productModuleForWorkspaceType` is the supported way
 * in. A raw record invites `MAP[type]`, which answers `undefined` for an
 * unknown type where the function answers `null` — and `undefined` compared
 * against a module key is the silent never-match this file exists to prevent.
 */
export const WORKSPACE_TYPE_TO_PRODUCT_MODULE = {
    solve: "solve",
    vsm: "vsm",
    hoshin: "hoshin",
    hoshin_workshop: "hoshin",
    lama: "lama",
};
export function productModuleForWorkspaceType(workspaceType) {
    return Object.hasOwn(WORKSPACE_TYPE_TO_PRODUCT_MODULE, workspaceType)
        ? (WORKSPACE_TYPE_TO_PRODUCT_MODULE[workspaceType] ?? null)
        : null;
}
//# sourceMappingURL=module-ids.js.map